// `Tree` + `PropertiesPanel` + `CommitBar` (design-system spec §3.7) — the
// structure-editor grammar, reusable for any tree-shaped editor. The plant map
// is the first consumer. Presentational only: rows are supplied as data, the
// caller owns selection/expansion state and every mutation.
//
//  - `TreeNodeRow` — 12.5 px row: disclosure caret, 15 px stroke type icon,
//    name 600, faint tabular meta, optional accent `draft` chip, inline 20 px
//    icon actions (revealed on hover on pointer devices, always present in the
//    DOM and always visible at phone widths — reordering must work on touch).
//  - `TreeGhostRow` — the dashed "+ Add …" contextual add.
//  - `PropertiesPanel` — the ~324 px right Card titled with the selection.
//  - `CommitBar` — white bottom bar: the plain-English sentence, a validation
//    hint, secondary/primary actions supplied by the caller.

import type { ReactNode } from "react";
import { Card } from "./card";
import { StatusChip } from "./status-chip";

export type TreeNodeKind = "facility" | "department" | "work_center" | "storage";

function NodeIcon({ kind }: { kind: TreeNodeKind }) {
  switch (kind) {
    case "facility":
      return (
        <svg viewBox="0 0 16 16">
          <path d="M2 13.5V6l4-2.5V13.5M6 6.8l4-2.3v9M10 7.2l4-1.7v8M2 13.5h12" />
        </svg>
      );
    case "department":
      return (
        <svg viewBox="0 0 16 16">
          <rect x="2.5" y="4" width="11" height="8.5" rx="1" />
          <path d="M2.5 7h11" />
        </svg>
      );
    case "work_center":
      return (
        <svg viewBox="0 0 16 16">
          <circle cx="8" cy="8" r="5.5" />
          <circle cx="8" cy="8" r="1.6" />
        </svg>
      );
    case "storage":
      return (
        <svg viewBox="0 0 16 16">
          <path d="M2.5 5.5 8 2.8l5.5 2.7v5L8 13.2l-5.5-2.7zM2.5 5.5 8 8.2m0 0 5.5-2.7M8 8.2v5" />
        </svg>
      );
  }
}

export interface TreeNodeRowProps {
  kind: TreeNodeKind;
  /** Node name, 600 ink (accent when selected). */
  name: ReactNode;
  /** 11 px faint tabular meta ("EXT-03 · 420 м/ч"). */
  meta?: ReactNode;
  /** Indent level: 0 facility, 1 department, 2 work center. */
  depth: 0 | 1 | 2;
  selected?: boolean;
  /** Accent `draft` chip label; omitted = committed node. */
  draftChip?: string | undefined;
  /** Disclosure caret: undefined = leaf (no caret). */
  expanded?: boolean | undefined;
  /** Accessible name for the caret button. */
  toggleLabel?: string;
  onToggle?: (() => void) | undefined;
  /** Select this node (row click / Enter on the name). */
  onSelect?: (() => void) | undefined;
  /** Accessible name of the row's select control. */
  selectLabel?: string;
  /** Inline 20 px icon actions (edit, add-child, move, remove). */
  actions?: ReactNode;
  children?: ReactNode;
  "data-testid"?: string;
  /** Machine-readable identity for walkthroughs (real id after commit). */
  "data-node-id"?: string | undefined;
  "data-node-path"?: string | undefined;
}

export function TreeNodeRow({
  kind,
  name,
  meta,
  depth,
  selected = false,
  draftChip,
  expanded,
  toggleLabel,
  onToggle,
  onSelect,
  selectLabel,
  actions,
  children,
  ...rest
}: TreeNodeRowProps) {
  const classes = ["ds-tnode", `ds-tnode-d${depth}`];
  if (selected) classes.push("is-sel");
  return (
    <li className="ds-tnode-li" {...rest}>
      <div className={classes.join(" ")}>
        {expanded !== undefined ? (
          <button
            type="button"
            className="ds-tnode-car"
            aria-expanded={expanded}
            aria-label={toggleLabel}
            onClick={onToggle}
          >
            <span aria-hidden="true">{expanded ? "▾" : "▸"}</span>
          </button>
        ) : (
          <span className="ds-tnode-car" aria-hidden="true" />
        )}
        <NodeIcon kind={kind} />
        {onSelect !== undefined ? (
          <button
            type="button"
            className="ds-tnode-nm"
            data-testid="tree-select"
            aria-pressed={selected}
            aria-label={selectLabel}
            onClick={onSelect}
          >
            {name}
          </button>
        ) : (
          <span className="ds-tnode-nm">{name}</span>
        )}
        {meta !== undefined ? <span className="ds-tnode-meta num">{meta}</span> : null}
        {draftChip !== undefined ? (
          <StatusChip variant="accent" dot={false} className="ds-tnode-chip">
            {draftChip}
          </StatusChip>
        ) : null}
        {actions !== undefined ? <span className="ds-tnode-hov">{actions}</span> : null}
      </div>
      {children}
    </li>
  );
}

/** The dashed contextual add-row ("+ Add work center to ОТК"). */
export function TreeGhostRow({
  depth,
  label,
  onAdd,
  ...rest
}: {
  depth: 0 | 1 | 2;
  label: ReactNode;
  onAdd: () => void;
  "data-testid"?: string;
}) {
  return (
    <li className={`ds-ghost ds-tnode-d${depth}`}>
      {/* The testid rides the BUTTON — the row is inert; the control is the
          contract a walkthrough clicks. */}
      <button type="button" onClick={onAdd} {...rest}>
        {label}
      </button>
    </li>
  );
}

/** A 20 px inline icon action on a tree row. */
export function TreeRowAction({
  label,
  glyph,
  disabled = false,
  onAct,
  ...rest
}: {
  label: string;
  glyph: ReactNode;
  disabled?: boolean;
  onAct: () => void;
  "data-testid"?: string;
}) {
  return (
    <button
      type="button"
      className="ds-tnode-hb"
      aria-label={label}
      title={label}
      disabled={disabled}
      onClick={onAct}
      {...rest}
    >
      <span aria-hidden="true">{glyph}</span>
    </button>
  );
}

/** The right-hand properties Card, titled with the selection (§3.7). */
export function PropertiesPanel({
  title,
  chip,
  children,
  ...rest
}: {
  title: ReactNode;
  /** Header chip (the accent `draft` marker). */
  chip?: ReactNode;
  children: ReactNode;
  "data-testid"?: string;
}) {
  return (
    <Card title={title} action={chip} className="ds-props-card" {...rest}>
      <div className="ds-props">{children}</div>
    </Card>
  );
}

/** The bottom commit bar: sentence + hint + the caller's actions (§3.7). */
export function CommitBar({
  sentence,
  hint,
  actions,
  ...rest
}: {
  /** The plain-English sentence saying exactly what the commit will do. */
  sentence: ReactNode;
  /** "Validated · no conflicts" or the blocking reason. */
  hint?: ReactNode;
  actions: ReactNode;
  "data-testid"?: string;
}) {
  return (
    <div className="ds-commitbar" {...rest}>
      <span className="ds-commit-sent">{sentence}</span>
      <div className="ds-commit-right">
        {hint !== undefined ? <span className="ds-commit-hint">{hint}</span> : null}
        {actions}
      </div>
    </div>
  );
}
